import type { AccountOption } from "@app/components/account-combobox";
import { BasisBadge, BasisDivergenceNote } from "@app/components/basis-switcher";
import ExportToExcelButton from "@app/components/export-to-excel-button";
import ExplainButton from "@app/components/explain/explain-button";
import type { ExplainMetric } from "@app/components/explain/explain-panel";
import type { XlsxCell, XlsxTable } from "@app/lib/export-xlsx";
import KpiChart from "@app/components/kpi-chart";
import type { StakeholderOption } from "@app/components/stakeholder-combobox";
import { BASIS_LABELS, basisParam, getBasis, useBasis } from "@app/lib/basis";
import { getPerspectiveId } from "@app/lib/perspective";
import { environment } from "@app/relay/relay-environment";
import { useToast } from "@app/components/toast";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { commitMutation, fetchQuery, graphql, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { pnlPage_Query } from "@app/queries/pnlPage_Query.graphql";
import type { pnlPage_ExportMutation } from "@app/queries/pnlPage_ExportMutation.graphql";
import PnlCellPanel, { type PnlCellScope } from "./pnl-cell-panel";
import { CellLink } from "@app/components/ui/cell-link";

export const pnlQuery = graphql`
  query pnlPage_Query($perspectiveId: ID, $basis: BasisEnum) {
    execComp(perspectiveId: $perspectiveId) {
      ttmExecComp
      ttmDistributions
      ttmEbitda
      adjustedEbitda
      adjustedEbitdaMargin
      monthlyTotal {
        month
        value
      }
      byStakeholder {
        name
        category
        ttm
      }
    }
    bankAccounts {
      id
      name
      nickname
      last4
      number
    }
    allStakeholders {
      id
      name
    }
    pnl(perspectiveId: $perspectiveId, basis: $basis) {
      revenue {
        month
        value
      }
      cogs {
        month
        value
      }
      grossProfit {
        month
        value
      }
      sAndM {
        month
        value
      }
      rAndD {
        month
        value
      }
      gAndA {
        month
        value
      }
      uncategorized {
        month
        value
      }
      ebitda {
        month
        value
      }
      taxes {
        month
        value
      }
      distributions {
        month
        value
      }
      afterDistributions {
        month
        value
      }
      interestAndFees {
        month
        value
      }
      loanProceeds {
        month
        value
      }
      loanPrincipalRepaid {
        month
        value
      }
      categories {
        name
        pnlLine
        category {
          id
        }
        monthly {
          month
          value
        }
      }
      cac {
        month
        value
      }
      cacTtm {
        month
        value
      }
    }
  }
`;

type Point = {
  readonly month: string;
  readonly value: number | null | undefined;
};

const MONTH_NUMBERS = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"] as const;
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

// Inclusive last day of a "YYYY-MM" month, as YYYY-MM-DD.
const endOfMonth = (month: string) => {
  const [y, m] = month.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  return `${month}-${String(last).padStart(2, "0")}`;
};

const isRealStakeholder = (s: { name: string }) =>
  !s.name.startsWith("&") &&
  !s.name.toLowerCase().includes("root stakeholder") &&
  s.name !== "_root";

function formatMoney(value: number | null | undefined): string {
  if (value == null || value === 0) return "—";
  const abs = Math.abs(value);
  const text =
    abs >= 1_000_000
      ? `${(abs / 1_000_000).toFixed(2)}M`
      : abs >= 1_000
      ? `${(abs / 1_000).toFixed(1)}k`
      : abs.toFixed(0);
  return `${value < 0 ? "-" : ""}$${text}`;
}

function formatPercent(value: number | null | undefined): string {
  return value == null ? "—" : `${(value * 100).toFixed(0)}%`;
}

// The drillable P&L lines, mirroring Metrics::ProfitAndLoss (and PnlLineDrillEnum).
type PnlLine = PnlCellScope["line"];

type Pnl = pnlPage_Query["response"]["pnl"];
type PnlCategory = Pnl["categories"][number];

// One cost group that rolls up a P&L line and expands to its categories.
const COST_GROUPS: { line: PnlLine; label: string; key: keyof Pnl }[] = [
  { line: "COGS", label: "Cost of revenue (COGS)", key: "cogs" },
  { line: "S_AND_M", label: "Sales & marketing", key: "sAndM" },
  { line: "R_AND_D", label: "R&D (Engineering)", key: "rAndD" },
  { line: "G_AND_A", label: "G&A", key: "gAndA" },
  { line: "UNCATEGORIZED", label: "Uncategorized costs", key: "uncategorized" },
];

type DisplayRow = {
  id: string;
  label: string;
  kind: "money" | "percent";
  strong?: boolean;
  indent?: 0 | 1 | 2;
  memo?: boolean; // a financing cash movement: shown, but never part of any subtotal
  group?: string; // pnl_line of an expandable group header
  expandable?: boolean;
  valueAt: (monthKey: string) => number | null;
  total: number | null;
  drill: { line: PnlLine; categoryId: string | null } | null;
};

const seriesMap = (points: ReadonlyArray<Point>) =>
  new Map(points.map((p) => [p.month, p.value ?? 0] as const));

const sumOver = (map: ReadonlyMap<string, number>, months: string[]) =>
  months.reduce((sum, month) => sum + (map.get(month) ?? 0), 0);

// Kicks off the whole-statement workbook (every counterparty, every month) as a
// background run. Deliberately NOT the same button as "Export to Excel": that one
// posts the grid on screen and hands back a file in a second, this one is minutes
// of full-corpus attribution and lands on /exports.
const fullExportMutation = graphql`
  mutation pnlPage_ExportMutation($perspectiveId: ID, $basis: BasisEnum) {
    exportCreate(kind: "PNL_FULL", perspectiveId: $perspectiveId, basis: $basis) {
      export {
        id
        status
      }
      errors {
        message
      }
    }
  }
`;

function FullPnlExportButton() {
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  const start = () => {
    setBusy(true);
    commitMutation<pnlPage_ExportMutation>(environment, {
      mutation: fullExportMutation,
      variables: {
        perspectiveId: getPerspectiveId() ?? undefined,
        basis: basisParam(getBasis()) ?? undefined,
      },
      onCompleted: (response, errors) => {
        setBusy(false);
        const failure = (errors ?? response.exportCreate?.errors)?.[0]?.message;
        if (failure) {
          toast({ message: failure, kind: "error" });
          return;
        }
        // The mutation is single-flight: pressing it while a run is already going
        // hands back that run rather than starting a second one, so the message
        // says "generating", not "started".
        toast({
          message: "Generating the full P&L — it takes a few minutes.",
          kind: "success",
        });
        navigate({ to: "/exports" });
      },
      onError: (error) => {
        setBusy(false);
        toast({ message: error.message || "Could not start the export.", kind: "error" });
      },
    });
  };

  return (
    <button
      type="button"
      onClick={start}
      disabled={busy}
      title="Generate the whole statement — every category and every counterparty, month by month, across every year"
      className="px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 text-sm whitespace-nowrap"
    >
      {busy ? "Starting…" : "Export full P&L"}
    </button>
  );
}

export const CURRENT_YEAR = new Date().getFullYear();

export default function PnlPage({ queryRef }: { queryRef: PreloadedQuery<pnlPage_Query> }) {
  const preloaded = usePreloadedQuery(pnlQuery, queryRef);
  const [data, setData] = useState(preloaded);
  const pnl = data.pnl;
  const basis = useBasis();

  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [yearOverride, setYearOverride] = useState<number | null>(null);
  const [cell, setCell] = useState<PnlCellScope | null>(null);

  // Re-sync the local mirror only when the route hands a NEW query (a perspective/
  // basis switch), keyed on queryRef — NOT on `preloaded`: an imperative refresh()
  // writes the same query root into the store, and keying on `preloaded` would
  // re-emit the stale initial snapshot and clobber the refreshed numbers.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setData(preloaded), [queryRef]);

  // A fix in the drill-down re-derives server-side (Pipeline::Delta); refetch the
  // statement so the table and charts reflect it. Same query root + variables as the
  // loader, so the mirror never diverges.
  const refresh = () =>
    fetchQuery<pnlPage_Query>(environment, pnlQuery, {
      perspectiveId: getPerspectiveId() ?? undefined,
      basis: basisParam(getBasis()) ?? undefined,
    })
      .toPromise()
      .then((fresh) => {
        if (fresh) setData(fresh);
      });

  const toggle = (line: string) =>
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(line)) next.delete(line);
      else next.add(line);
      return next;
    });

  if (pnl.revenue.length === 0) {
    return <div className="p-6 text-gray-500">No data yet — import statements first.</div>;
  }

  // Years that carry data (every line spans the same months, so revenue is enough),
  // and the active year — newest by default, navigable with ‹ ›.
  const years = [...new Set(pnl.revenue.map((p) => Number(p.month.slice(0, 4))))].sort((a, b) => a - b);
  const minYear = years[0];
  const maxYear = years[years.length - 1];
  const year = yearOverride == null ? maxYear : Math.min(maxYear, Math.max(minYear, yearOverride));
  const months = MONTH_NUMBERS.map((m) => `${year}-${m}`);

  const accountOptions: AccountOption[] = data.bankAccounts.map((a) => ({
    id: a.id,
    name: a.name,
    nickname: a.nickname,
    last4: a.last4,
  }));
  const stakeholderOptions: StakeholderOption[] = data.allStakeholders.filter(isRealStakeholder);

  const categoriesByLine = new Map<string, PnlCategory[]>();
  for (const category of pnl.categories) {
    const list = categoriesByLine.get(category.pnlLine) ?? [];
    list.push(category);
    categoriesByLine.set(category.pnlLine, list);
  }

  const revenueMap = seriesMap(pnl.revenue);

  const moneyRow = (
    id: string,
    label: string,
    points: ReadonlyArray<Point>,
    line: PnlLine,
    opts: { strong?: boolean; indent?: 0 | 1 | 2; memo?: boolean } = {}
  ): DisplayRow => {
    const map = seriesMap(points);
    return {
      id,
      label,
      kind: "money",
      strong: opts.strong,
      indent: opts.indent,
      memo: opts.memo,
      valueAt: (month) => map.get(month) ?? 0,
      total: sumOver(map, months),
      drill: { line, categoryId: null },
    };
  };

  // Margins are recomputed from the bucketed money (revenue per period), never
  // averaged — and they are not a sum of transactions, so they do not drill.
  const marginRow = (id: string, label: string, numerator: ReadonlyArray<Point>): DisplayRow => {
    const numMap = seriesMap(numerator);
    const revenueTotal = sumOver(revenueMap, months);
    return {
      id,
      label,
      kind: "percent",
      indent: 1,
      valueAt: (month) => {
        const revenue = revenueMap.get(month) ?? 0;
        return revenue === 0 ? null : (numMap.get(month) ?? 0) / revenue;
      },
      total: revenueTotal === 0 ? null : sumOver(numMap, months) / revenueTotal,
      drill: null,
    };
  };

  const hasFinancing = [pnl.interestAndFees, pnl.loanProceeds, pnl.loanPrincipalRepaid].some((series) =>
    series.some((point) => (point.value ?? 0) !== 0)
  );

  const rows: DisplayRow[] = [];
  rows.push(moneyRow("revenue", "Revenue", pnl.revenue, "REVENUE", { strong: true }));

  const pushGroup = (line: PnlLine, label: string, points: ReadonlyArray<Point>) => {
    const categories = categoriesByLine.get(line) ?? [];
    rows.push({
      ...moneyRow(`group:${line}`, label, points, line, { indent: 1 }),
      group: line,
      expandable: categories.length > 0,
    });
    if (expanded.has(line)) {
      for (const category of categories) {
        const map = seriesMap(category.monthly);
        rows.push({
          id: `cat:${line}:${category.name}`,
          label: category.name,
          kind: "money",
          indent: 2,
          valueAt: (month) => map.get(month) ?? 0,
          total: sumOver(map, months),
          drill: { line, categoryId: category.category?.id ?? null },
        });
      }
    }
  };

  pushGroup("COGS", "Cost of revenue (COGS)", pnl.cogs);
  rows.push(moneyRow("gross_profit", "Gross profit", pnl.grossProfit, "GROSS_PROFIT", { strong: true }));
  rows.push(marginRow("gross_margin", "Gross margin", pnl.grossProfit));
  for (const group of COST_GROUPS.slice(1)) {
    pushGroup(group.line, group.label, pnl[group.key] as ReadonlyArray<Point>);
  }
  rows.push(moneyRow("ebitda", "EBITDA", pnl.ebitda, "EBITDA", { strong: true }));
  rows.push(marginRow("ebitda_margin", "EBITDA margin", pnl.ebitda));
  pushGroup("TAXES", "Taxes", pnl.taxes);
  // Financing, below the operating line. Interest is a real cost that simply is not an
  // operating one; the other two are MEMO — borrowing is not income and repaying principal
  // is not an expense, so neither is summed into a profit line. They are here so a loan
  // deliberately absent from Revenue is still visible on the statement it is missing from.
  //
  // Only for a company that has actually borrowed, and gated on the WHOLE series rather
  // than the selected year, so the block doesn't blink in and out as you page through years.
  if (hasFinancing) {
    rows.push(moneyRow("interest_and_fees", "Interest & financing fees", pnl.interestAndFees, "INTEREST_AND_FEES", { indent: 1 }));
    rows.push(moneyRow("loan_proceeds", "Loan proceeds drawn (memo)", pnl.loanProceeds, "LOAN_PROCEEDS", { indent: 1, memo: true }));
    rows.push(moneyRow("loan_principal_repaid", "Loan principal repaid (memo)", pnl.loanPrincipalRepaid, "LOAN_PRINCIPAL_REPAID", { indent: 1, memo: true }));
  }
  rows.push(moneyRow("distributions", "Owner distributions", pnl.distributions, "DISTRIBUTIONS", { indent: 1 }));
  rows.push(
    moneyRow("after_distributions", "After distributions", pnl.afterDistributions, "AFTER_DISTRIBUTIONS", {
      strong: true,
    })
  );

  const indentClass = ["", "pl-6", "pl-10"] as const;

  // Open the drill-down for a clicked cell: a month column scopes to that month,
  // the Total column to the whole year.
  const openCell = (row: DisplayRow, monthKey: string | null) => {
    if (!row.drill) return;
    const [from, to, period] = monthKey
      ? [`${monthKey}-01`, endOfMonth(monthKey), `${MONTH_LABELS[Number(monthKey.slice(5, 7)) - 1]} ${year}`]
      : [`${year}-01-01`, `${year}-12-31`, String(year)];
    setCell({ line: row.drill.line, categoryId: row.drill.categoryId, from, to, label: `${row.label} · ${period}` });
  };

  const cellButton = (row: DisplayRow, value: number | null, monthKey: string | null) => {
    const text = row.kind === "percent" ? formatPercent(value) : formatMoney(value);
    const drillable = row.drill != null && value != null && value !== 0;
    if (!drillable) return text;
    return (
      <CellLink
        onClick={() => openCell(row, monthKey)}
        className="decoration-dotted underline-offset-2 hover:text-inherit"
        title="View the transactions behind this number"
      >
        {text}
      </CellLink>
    );
  };

  const yearMargins = (numerator: ReadonlyArray<Point>): Point[] => {
    const numMap = seriesMap(numerator);
    return months.map((month) => {
      const revenue = revenueMap.get(month) ?? 0;
      return { month, value: revenue === 0 ? null : (numMap.get(month) ?? 0) / revenue };
    });
  };

  const yearSeries = (points: ReadonlyArray<Point>): Point[] => {
    const map = seriesMap(points);
    return months.map((month) => ({ month, value: map.get(month) ?? 0 }));
  };

  // The exact grid on screen: line rows (indented to show the hierarchy) over the
  // selected year's 12 months + the year total. Margin rows stay percent inside
  // the money columns via a per-cell type override.
  const exportCell = (row: DisplayRow, value: number | null): XlsxCell =>
    row.kind === "percent" ? { v: value, t: "percent" } : value;

  const buildExport = (): XlsxTable => ({
    filename: `pnl-${year}`,
    sheetName: `P&L ${year}`,
    columns: [
      { label: "Line", type: "text" },
      ...MONTH_LABELS.map((m) => ({ label: m, type: "money" as const })),
      { label: String(year), type: "money" as const },
    ],
    rows: rows.map((row) => [
      "  ".repeat(row.indent ?? 0) + row.label,
      ...months.map((month) => exportCell(row, row.valueAt(month))),
      exportCell(row, row.total),
    ]),
  });

  return (
    <div className="p-6 max-w-full mx-auto space-y-6">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-xl font-bold">P&L statement ({BASIS_LABELS[basis].toLowerCase()} basis)</h1>
        <BasisBadge />
        <ExportToExcelButton build={buildExport} className="ml-auto px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 text-sm whitespace-nowrap" />
        <FullPnlExportButton />
        <Link
          to="/exports"
          className="px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm whitespace-nowrap"
          title="Every workbook this account has generated"
        >
          Exports
        </Link>
        <div className="flex items-center gap-1 text-sm">
          <button
            type="button"
            disabled={year <= minYear}
            onClick={() => setYearOverride(year - 1)}
            className="px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            title="Previous year"
          >
            ‹
          </button>
          <span className="px-2 tabular-nums font-medium text-gray-800">{year}</span>
          <button
            type="button"
            disabled={year >= maxYear}
            onClick={() => setYearOverride(year + 1)}
            className="px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            title="Next year"
          >
            ›
          </button>
        </div>
      </div>

      <KpiChart
        title={`EBITDA · ${year}`}
        series={[
          { name: "EBITDA", points: yearSeries(pnl.ebitda), type: "bar", color: "#7c3aed" },
          { name: "After distributions", points: yearSeries(pnl.afterDistributions), type: "line", color: "#16a34a" },
        ]}
        format="money"
        onMonthClick={() => {}}
      />

      <div className="overflow-x-auto bg-white rounded border">
        <table className="text-xs w-full">
          <thead>
            <tr className="border-b text-gray-500">
              <th className="text-left px-3 py-2 sticky left-0 bg-white">Line</th>
              {months.map((month, i) => (
                <th key={month} className="px-2 py-2 text-right font-normal whitespace-nowrap">
                  {/* The month header opens that month broken down to the counterparty
                      grain — where the categorization is actually corrected. */}
                  <Link
                    to="/pnl/$month"
                    params={{ month }}
                    className="hover:underline decoration-dotted underline-offset-2 hover:text-gray-800"
                    title={`Open ${MONTH_LABELS[i]} ${year} broken down by counterparty`}
                  >
                    {MONTH_LABELS[i]}
                  </Link>
                </th>
              ))}
              <th className="px-2 py-2 text-right font-semibold whitespace-nowrap border-l">{year}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className={`border-b last:border-0 ${row.strong ? "font-semibold bg-gray-50" : ""} ${row.memo ? "text-gray-400 italic" : ""}`}
              >
                <td
                  className={`px-3 py-1.5 sticky left-0 whitespace-nowrap ${
                    row.strong ? "bg-gray-50" : "bg-white"
                  } ${row.indent ? `${indentClass[row.indent]} text-gray-600` : ""}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {row.expandable ? (
                      <button
                        type="button"
                        onClick={() => toggle(row.group as string)}
                        className="w-4 shrink-0 text-gray-400 hover:text-gray-700"
                        title={expanded.has(row.group as string) ? "Collapse" : "Expand"}
                      >
                        {expanded.has(row.group as string) ? "▾" : "▸"}
                      </button>
                    ) : (
                      row.indent === 1 && <span className="w-4 shrink-0" />
                    )}
                    {row.label}
                  </span>
                </td>
                {months.map((month) => (
                  <td key={month} className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">
                    {cellButton(row, row.valueAt(month), month)}
                  </td>
                ))}
                <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap font-semibold border-l">
                  {cellButton(row, row.total, null)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400">
        Click a month header to open that month broken down to the counterparty level, where categories can be corrected
        in place. Click any number to see the transactions behind it — and re-categorize, change the counterparty, mark
        internal, or send to needs-review. Costs roll up through each counterparty to its category (Review page); expand
        a P&L line to see its categories.
      </p>

      <BasisDivergenceNote />

      <OwnerEarningsSection execComp={data.execComp} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <KpiChart
          title={`Margins · ${year}`}
          series={[
            { name: "Gross margin", points: yearMargins(pnl.grossProfit), type: "line", color: "#16a34a" },
            { name: "EBITDA margin", points: yearMargins(pnl.ebitda), type: "line", color: "#7c3aed" },
          ]}
          format="percent"
          onMonthClick={() => {}}
        />
        <KpiChart
          title={`CAC (fully-loaded S&M / new customers) · ${year}`}
          series={[
            { name: "Monthly", points: yearSeries(pnl.cac), type: "bar", color: "#fb923c" },
            { name: "TTM", points: yearSeries(pnl.cacTtm), type: "line", color: "#dc2626" },
          ]}
          format="money"
          onMonthClick={() => {}}
        />
      </div>

      {cell && (
        <PnlCellPanel
          scope={cell}
          accountOptions={accountOptions}
          stakeholderOptions={stakeholderOptions}
          onClose={() => setCell(null)}
          onChanged={() => refresh()}
        />
      )}
    </div>
  );
}

function OwnerEarningsSection({ execComp }: { execComp: pnlPage_Query["response"]["execComp"] }) {
  const cards: {
    label: string;
    value: string;
    hint: string;
    explain?: ExplainMetric;
  }[] = [
    {
      label: "Exec comp (TTM)",
      value: `$${execComp.ttmExecComp.toLocaleString()}`,
      hint: "salaries, perks, travel, health",
    },
    {
      label: "Distributions (TTM)",
      value: `$${execComp.ttmDistributions.toLocaleString()}`,
      hint: "owner draws (below the EBITDA line)",
    },
    { label: "EBITDA (TTM)", value: `$${execComp.ttmEbitda.toLocaleString()}`, hint: "as reported", explain: "EBITDA" },
    {
      label: "Adjusted EBITDA",
      value: `$${execComp.adjustedEbitda.toLocaleString()}`,
      hint: "exec comp added back (SDE)",
    },
    {
      label: "Adjusted margin",
      value:
        execComp.adjustedEbitdaMargin == null ? "—" : `${(execComp.adjustedEbitdaMargin * 100).toFixed(1)}%`,
      hint: "of TTM revenue",
    },
  ];

  return (
    <div className="space-y-4">
      <h2 className="font-bold">Owner earnings & add-backs</h2>
      <p className="text-sm text-gray-500">
        Categorize executives under "Executive compensation" on the Review page. Adjusted EBITDA adds their full cost
        back — the seller-side view an acquirer starts from.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {cards.map((card) => (
          <div key={card.label} className="bg-white rounded border p-4">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs text-gray-500">{card.label}</p>
              {card.explain && <ExplainButton metric={card.explain} title={card.label} />}
            </div>
            <p className="text-2xl font-bold mt-1">{card.value}</p>
            <p className="text-[11px] text-gray-400 mt-1">{card.hint}</p>
          </div>
        ))}
      </div>

      {execComp.byStakeholder.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-gray-500 text-xs">
                  <th className="text-left px-3 py-2">Executive</th>
                  <th className="text-left px-3 py-2">Category</th>
                  <th className="text-right px-3 py-2">TTM cost</th>
                </tr>
              </thead>
              <tbody>
                {execComp.byStakeholder.map((row) => (
                  <tr key={`${row.name}-${row.category}`} className="border-b last:border-0">
                    <td className="px-3 py-2">{row.name}</td>
                    <td className="px-3 py-2 text-gray-500">{row.category}</td>
                    <td className="px-3 py-2 text-right tabular-nums">${row.ttm.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <KpiChart
            title="Executive compensation (monthly)"
            series={[{ name: "Exec comp", points: execComp.monthlyTotal, type: "bar", color: "#7c3aed" }]}
            format="money"
            onMonthClick={() => {}}
          />
        </div>
      )}
    </div>
  );
}
